import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

export type ThemePreference = 'system' | 'light' | 'dark';
export type EffectiveTheme = 'light' | 'dark';

type ThemeContextValue = {
  themePreference: ThemePreference;
  effectiveTheme: EffectiveTheme;
  setThemePreference: (theme: ThemePreference) => void;
  cycleThemePreference: () => void;
};

const STORAGE_KEY = 'modalos-theme-preference';

const ThemeContext = createContext<ThemeContextValue>({
  themePreference: 'system',
  effectiveTheme: 'light',
  setThemePreference: () => undefined,
  cycleThemePreference: () => undefined,
});

function getSystemTheme(): EffectiveTheme {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return 'light';
  }

  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function getStoredThemePreference(): ThemePreference {
  if (typeof window === 'undefined') return 'system';

  const storedValue = window.localStorage.getItem(STORAGE_KEY);
  if (storedValue === 'system' || storedValue === 'light' || storedValue === 'dark') {
    return storedValue;
  }

  return 'system';
}

export function useTheme() {
  return useContext(ThemeContext);
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [themePreference, setThemePreferenceState] = useState<ThemePreference>(() => getStoredThemePreference());
  const [systemTheme, setSystemTheme] = useState<EffectiveTheme>(() => getSystemTheme());

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (event: MediaQueryListEvent) => {
      setSystemTheme(event.matches ? 'dark' : 'light');
    };

    setSystemTheme(mediaQuery.matches ? 'dark' : 'light');

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }

    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  const effectiveTheme = themePreference === 'system' ? systemTheme : themePreference;

  useEffect(() => {
    if (typeof document === 'undefined') return;

    const root = document.documentElement;
    root.dataset.theme = effectiveTheme;
    root.dataset.themePreference = themePreference;
    root.style.colorScheme = effectiveTheme;
  }, [effectiveTheme, themePreference]);

  const setThemePreference = useCallback((theme: ThemePreference) => {
    setThemePreferenceState(theme);

    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEY, theme);
    }
  }, []);

  const cycleThemePreference = useCallback(() => {
    const nextTheme: ThemePreference =
      themePreference === 'system' ? 'dark' : themePreference === 'dark' ? 'light' : 'system';
    setThemePreference(nextTheme);
  }, [setThemePreference, themePreference]);

  const value = useMemo(
    () => ({
      themePreference,
      effectiveTheme,
      setThemePreference,
      cycleThemePreference,
    }),
    [cycleThemePreference, effectiveTheme, setThemePreference, themePreference],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}